/**
 * Global keyboard shortcuts. Single keys never fire while typing in a field.
 *
 *   Mod+K        command palette (works everywhere)
 *   g then o/s/a/p/r/m/u   jump to a section (see NAV)
 *   n            New session (runs the 'session.new' command when a page registers it)
 *   c            New item (opens Missions with the New item dialog)
 *   [            collapse or expand the rail
 *   ?            shortcuts help
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { getCommand, runCommand, togglePalette, useCommandStore } from './commands.ts';
import { NAV, SECONDARY_NAV } from './nav.ts';
const ALL_NAV = [...NAV, ...SECONDARY_NAV];
import { hasMod, isTypingTarget } from '@orgus/ui';
import { usePrefs } from './prefs.ts';
import { openNewItem } from '../features/palette/launch.ts';
import { toast } from '@orgus/ui';

export const SEQUENCE_TIMEOUT_MS = 1200;

export interface ShortcutHelp {
  keys: string[];
  then?: boolean;
  label: string;
  group: 'General' | 'Navigation' | 'Actions';
}

export const SHORTCUT_HELP: ShortcutHelp[] = [
  { keys: ['mod', 'k'], label: 'Search and run commands', group: 'General' },
  { keys: ['?'], label: 'Show keyboard shortcuts', group: 'General' },
  { keys: ['['], label: 'Collapse or expand the sidebar', group: 'General' },
  { keys: ['esc'], label: 'Close a dialog, menu or drawer', group: 'General' },
  ...ALL_NAV.map((n): ShortcutHelp => ({ keys: ['g', n.key], then: true, label: `Go to ${n.label}`, group: 'Navigation' })),
  { keys: ['n'], label: 'New session', group: 'Actions' },
  { keys: ['c'], label: 'New item', group: 'Actions' },
  { keys: ['f'], label: 'Filter the list in words', group: 'Actions' },
  { keys: ['x'], label: 'Select the focused row (Shift-click selects a range)', group: 'Actions' },
  { keys: ['?'], label: 'In the palette: ask about live state', group: 'General' },
  { keys: ['>'], label: 'In the palette: actions only', group: 'General' },
  { keys: ['mod', 's'], label: 'In the palette: save the answer as a view', group: 'General' },
];

/** Installs the global handlers. Returns whether a g-sequence is pending (for the hint). */
export function useGlobalShortcuts(): { pendingG: boolean } {
  const navigate = useNavigate();
  const [pendingG, setPendingG] = useState(false);

  useEffect(() => {
    let gTimer: ReturnType<typeof setTimeout> | null = null;
    let gArmed = false;
    const disarm = () => {
      gArmed = false;
      setPendingG(false);
      if (gTimer) clearTimeout(gTimer);
      gTimer = null;
    };

    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.isComposing) return;
      if (hasMod(e) && !e.altKey && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        disarm();
        togglePalette();
        return;
      }
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (isTypingTarget(e.target)) return;
      if (useCommandStore.getState().paletteOpen) return;
      // A dialog or menu owns the keyboard while open.
      if (document.querySelector('[role="dialog"][data-state="open"], [role="menu"][data-state="open"]')) return;

      const key = e.key;
      if (gArmed) {
        const item = ALL_NAV.find((n) => n.key === key.toLowerCase());
        disarm();
        if (item) {
          e.preventDefault();
          navigate(item.to);
        }
        return;
      }
      if (key === 'g') {
        gArmed = true;
        setPendingG(true);
        gTimer = setTimeout(disarm, SEQUENCE_TIMEOUT_MS);
        return;
      }
      if (key === '?') {
        e.preventDefault();
        usePrefs.getState().setHelpOpen(true);
        return;
      }
      if (key === '[') {
        e.preventDefault();
        usePrefs.getState().toggleRail();
        return;
      }
      // c: New item, like Linear. On Missions the page binds c itself.
      if (key === 'c' && !window.location.pathname.startsWith('/missions')) {
        e.preventDefault();
        openNewItem(navigate);
        return;
      }
      if (key === 'n') {
        e.preventDefault();
        if (!getCommand('session.new')) {
          toast('New session is not available yet', { description: 'Start Claude Code in a terminal for now; it shows up here on its own.' });
          return;
        }
        runCommand('session.new');
      }
    };

    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      disarm();
    };
  }, [navigate]);

  return { pendingG };
}
